import { useRef, useState } from "react";
import { fetchLink, linkProblem, pickedFromFile, pickedFromSlide, PHOTO_TYPES, type Fetched, type Picked } from "../lib/flyer";
import { loneReel, reelFrom } from "../lib/flyerViewer";
import { useApp } from "./appState";
import { Flyer } from "./Flyer";

/**
 * Where a flyer comes from: a photo on this device or a pasted link. An
 * Instagram carousel asks which slide is the flyer. `onPick` may be async
 * (read it, store it); its error shows here.
 */
export function FlyerSource({ onPick, label = "Choose a photo", primary = false, busyText = "Loading…" }: {
  onPick: (p: Picked) => void | Promise<void>;
  label?: string;
  primary?: boolean;
  busyText?: string;
}) {
  const { zoom } = useApp();
  const input = useRef<HTMLInputElement>(null);
  const [link, setLink] = useState("");
  const [post, setPost] = useState<Fetched | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const take = (p: Picked) =>
    run(async () => {
      await onPick(p);
      setPost(null);
      setLink("");
    });
  const takeLink = () => {
    const problem = linkProblem(link);
    if (problem) {
      setError(problem);
      return;
    }
    void run(async () => {
      const got = await fetchLink(link);
      if (got.slides.length === 1) {
        await onPick(pickedFromSlide(got.slides[0], got.caption));
        setLink("");
      } else setPost(got);
    });
  };

  return (
    <div className="fsrc">
      <input ref={input} type="file" accept={PHOTO_TYPES} hidden aria-label="Flyer photo"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) void run(async () => onPick(await pickedFromFile(file)));
        }} />
      <button type="button" className={`btn sm ${primary ? "primary" : "ghost"}`} disabled={busy} onClick={() => input.current?.click()}>
        {label}
      </button>
      <div className="fsrc-link">
        <input type="url" inputMode="url" value={link} placeholder="or paste an Instagram post or image link" aria-label="Flyer link"
          onChange={(e) => setLink(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              takeLink();
            }
          }} />
        <button type="button" className="btn sm" disabled={busy || !link.trim()} onClick={takeLink}>Use</button>
      </div>
      {post && (
        <div className="slides" role="group" aria-label="Instagram slides">
          <span>Which slide is the flyer?</span>
          <div className="slide-grid">
            {post.slides.map((s, i) => (
              <div className="slide" key={i}>
                <Flyer url={s.data_url} alt={`Slide ${i + 1}`} natural
                  onZoom={() => zoom(reelFrom(post.slides, s, (sl) => ({ url: sl.data_url, title: `Slide ${post.slides.indexOf(sl) + 1} of ${post.slides.length}` })))} />
                <button type="button" className="btn sm" disabled={busy} onClick={() => void take(pickedFromSlide(s, post.caption))}>
                  Use {i + 1}
                </button>
              </div>
            ))}
          </div>
          <button type="button" className="btn ghost sm" onClick={() => setPost(null)}>Cancel</button>
        </div>
      )}
      {busy && <p className="loading">{busyText}</p>}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

/** The Log / Edit sheets' Flyer field: what the show has, or will have once saved, and ways to change it. */
export function FlyerField({ url, picked, search, onPick, onRemove, title }: {
  url: string | null;
  picked: Picked | null;
  search: string;
  onPick: (p: Picked) => void;
  onRemove: () => void;
  /** What the viewer's bar calls this flyer. */
  title?: string;
}) {
  const { zoom } = useApp();
  const shown = picked?.preview ?? url;
  return (
    <div className="f">
      <span>Flyer{picked ? " · stored when you save" : ""}</span>
      <div className="flyer-field">
        <div>
          {shown ? (
            <Flyer url={shown} alt="Flyer"
              onZoom={() => zoom(loneReel(shown, title?.trim() || "This show's flyer"))} />
          ) : (
            <div className="flyer none"><span>None</span></div>
          )}
        </div>
        <div className="ff-tools">
          <FlyerSource onPick={onPick} label={shown ? "Replace with a photo" : "Choose a photo"} />
          <div className="sheet-acts">
            <a className="btn ghost sm" href={search} target="_blank" rel="noopener noreferrer">Search Google Images</a>
            {shown && <button type="button" className="btn ghost sm" onClick={onRemove}>Remove</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
